import React from "react";
import { motion } from "framer-motion";
import { TrendingDown, CalendarClock, Clock, KeyRound } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/motion";

const ITEMS = [
  { icon: TrendingDown, text: "Sei quasi sempre pieno, ma a fine stagione i numeri non ti soddisfano." },
  { icon: CalendarClock, text: "Tieni lo stesso prezzo tutto l'anno, anche quando la domanda schizza." },
  { icon: Clock, text: "Gestisci tutto da solo tra OTA, messaggi e pulizie, e il tempo non basta mai." },
  { icon: KeyRound, text: "Hai appena comprato o ristrutturato e non sai da dove partire per aprire." },
];

export const Problema = () => (
  <section id="problema" data-testid="section-problema" className="relative overflow-hidden bg-cream py-24 md:py-32">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 select-none whitespace-nowrap opacity-[0.04]"
      style={{
        WebkitMaskImage: "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
        maskImage: "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
      }}
    >
      <div className="alba-marquee flex">
        {[0, 1].map((k) => (
          <span key={k} className="px-6 font-display text-[13vw] font-semibold uppercase leading-none text-navy">
            Occupazione piena · Marginalità piena · Occupazione piena · Marginalità piena ·&nbsp;
          </span>
        ))}
      </div>
    </div>

    <div className="relative mx-auto max-w-4xl px-6 md:px-12">
      <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="text-center">
        <motion.p variants={fadeUp} className="mb-5 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
          Il problema
        </motion.p>
        <motion.h2 variants={fadeUp} className="font-display text-4xl font-medium tracking-tight text-navy sm:text-5xl">
          Il tuo immobile lavora.<br />Ma sta rendendo davvero?
        </motion.h2>
        <motion.p variants={fadeUp} className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-text-muted sm:text-lg">
          Occupazione piena non significa sempre marginalità piena. Spesso il prezzo resta fermo mentre
          il mercato si muove.
        </motion.p>
      </motion.div>

      <motion.p
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
        className="mt-16 mb-8 font-display text-2xl font-medium text-navy"
      >
        Ti riconosci?
      </motion.p>

      <motion.ul variants={staggerContainer} initial="hidden" whileInView="show" viewport={viewportOnce} className="space-y-4">
        {ITEMS.map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.li
              key={i}
              variants={fadeUp}
              data-testid={`problema-item-${i}`}
              className="group flex items-start gap-5 rounded-2xl border border-navy/10 bg-white p-5 shadow-[0_8px_30px_rgba(31,58,95,0.05)] transition-transform duration-300 hover:-translate-y-1 md:p-6"
            >
              <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cream text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-white">
                <Icon className="h-5 w-5" strokeWidth={1.8} />
              </span>
              <span className="pt-1.5 text-base leading-relaxed text-navy-deep">{item.text}</span>
            </motion.li>
          );
        })}
      </motion.ul>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={viewportOnce}
        className="mt-12 rounded-2xl border-l-4 border-gold bg-navy p-8 md:p-10"
      >
        <p className="text-lg leading-relaxed text-white/90">
          Se ti riconosci anche in un solo punto, c'è qualcosa da recuperare: margine lasciato sul
          tavolo da un prezzo fermo, tempo perso a gestire tutto da solo, o semplicemente la partenza
          giusta da dare a una struttura nuova.{" "}
          <span className="text-gold">La buona notizia:</span> si lavora su prezzo, distribuzione,
          gestione e avvio — non abbassando le tariffe.
        </p>
      </motion.div>
    </div>
  </section>
);
